import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Chip,
  Skeleton,
  Divider,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import logStore from "../../stores/LogStore";
import deviceStore from "../../stores/DeviceStore";

const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "text.secondary", letterSpacing: 0.2 }}
  >
    {children}
  </Typography>
);

const Value = ({ children }) => (
  <Typography variant="body2" sx={{ fontWeight: 600 }}>
    {children}
  </Typography>
);

const PendingDeviceCard = ({ device, loading = false }) => {
  const navigate = useNavigate();

  const isConnected =
    !loading && logStore.connectedDeviceIds.has(device?.pimios_id);

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      deviceStore.deleteDevice(device.id);
    }
  };

  const handleCardClick = () => {
    if (device?.pimios_id) {
      navigate(`/devices/${device.pimios_id}-${device.name}`);
    }
  };

  return (
    <Box
      onClick={loading ? undefined : handleCardClick}
      sx={{
        cursor: loading ? "default" : "pointer",
        height: "100%",
        display: "flex",
      }}
    >
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          overflow: "hidden",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" height={140} />
        ) : (
          <CardMedia
            component="img"
            height="140"
            image={
              device.image_url ||
              device.type_image ||
              "https://placehold.co/150"
            }
            alt={device.name || "Device image"}
          />
        )}

        <CardContent sx={{ p: 2.25, flexGrow: 1 }}>
          {/* Header: Name */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            {loading ? (
              <Skeleton width="60%" height={28} />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  pr: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {device.name || "Unnamed Device"}
              </Typography>
            )}
          </Box>

          {/* Subheader: ID */}
          <Box sx={{ mb: 1.5 }}>
            {loading ? (
              <Skeleton width={160} />
            ) : (
              <>
                <Label>Device ID</Label>
                <Value>{device.pimios_id || "—"}</Value>
              </>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Details Grid */}
          <Grid container spacing={1.5}>
            {/* Type */}
            <Grid item xs={12} sm={6}>
              {loading ? (
                <>
                  <Skeleton width="40%" />
                  <Skeleton width="90%" />
                </>
              ) : (
                <>
                  <Label>Type</Label>
                  <Value>
                    {device.manufacture
                      ? `${device.manufacture} - ${device.type_name}`
                      : device.type_name || "—"}
                  </Value>
                </>
              )}
            </Grid>

            {/* Region */}
            <Grid item xs={12} sm={6}>
              {loading ? (
                <>
                  <Skeleton width="40%" />
                  <Skeleton width="80%" />
                </>
              ) : (
                <>
                  <Label>Region</Label>
                  <Value>{device.region_name || "—"}</Value>
                </>
              )}
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              {loading ? (
                <>
                  <Skeleton width="30%" />
                  <Skeleton width="100%" />
                </>
              ) : (
                <>
                  <Label>Location</Label>
                  <Value
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {device.location
                      ? [
                          device.location.street,
                          device.location.city,
                          device.location.state,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "—"}
                  </Value>
                </>
              )}
            </Grid>
            {/* Status — moved into the grid for alignment */}
            <Grid item xs={12}>
              {loading ? (
                <>
                  <Skeleton variant="text" width={50} height={20} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Skeleton variant="circular" width={10} height={10} />
                    <Skeleton width={120} />
                  </Box>
                </>
              ) : (
                <>
                  <Label>Status</Label>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: isConnected ? "#22C55E" : "#EF4444",
                      }}
                    />
                    <Typography variant="body2">
                      {isConnected ? "Connected" : "Not Connected"}
                    </Typography>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>

          {/* Missing fields */}
          {!loading && device.missingFields?.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {device.missingFields.map((field) => (
                <Chip
                  key={field}
                  label={`Missing ${field}`}
                  color="warning"
                  size="small"
                  sx={{ bgcolor: "#FEF3C7", color: "#92400E" }}
                />
              ))}
            </Box>
          )}
        </CardContent>

        {/* Actions pinned to bottom by the flex layout */}
        {!loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              sx={{
                color: "#22C55E",
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
              onClick={() => navigate(`/edit-unit/${device.id}`)}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              sx={{
                backgroundColor: "#F3F4F6",
                color: "#6B7280",
                "&:hover": { backgroundColor: "#E5E7EB" },
              }}
              onClick={handleDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Card>
    </Box>
  );
};

Label.propTypes = {
  children: PropTypes.node.isRequired,
};

Value.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

PendingDeviceCard.propTypes = {
  device: PropTypes.object,
  loading: PropTypes.bool,
  sx: PropTypes.object,
};

export default PendingDeviceCard;
